'use memo';

import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/lib/auth';
import { getShortcutDefinition, type ShortcutId } from '../registry/definitions';
import { matchesKeyCombo } from '../utils/keyCombo';
import { currentPlatform } from '../utils/platform';
type UseShortcutOptions = {
  /** scope が有効なときだけ true を渡す（launcher 開いている間のみ等）。既定 true */
  enabled?: boolean;
};

/**
 * registry 定義の id でショートカットを購読する。
 * 既存 useToggleAiChatByKeyPress の防御（repeat 無視・modifier 完全一致・
 * 登録キーのみ preventDefault）を標準化したもの（§4.3 shortcuts）。
 */
export function useShortcut(id: ShortcutId, handler: () => void, options?: UseShortcutOptions): void {
  const enabled = options?.enabled ?? true;
  const platform = currentPlatform();
  const {
    featureFlag
  } = getShortcutDefinition(id);
  // featureFlag ガード。リロード直後の古いセッションが /v2/session 再取得で更新されたとき
  // listener を張り直せるよう、非リアクティブな featureFlags.check ではなく auth ストアを購読する
  const flagEnabled = useAuthStore(state => featureFlag ? Boolean(state.session?.featureFlags?.[featureFlag]) : true);
  // handler は ref 経由で常に最新を呼ぶ（inline lambda でも listener を張り直さない）
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    const definition = getShortcutDefinition(id);
    if (definition.displayOnly || !enabled || !flagEnabled) return;
    const combo = definition.keys[platform];
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat && !definition.allowRepeat) return;
      if (event.isComposing) return;
      if (!matchesKeyCombo(event, combo)) return;
      if (definition.preventDefault !== false) {
        event.preventDefault();
      }
      handlerRef.current();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, flagEnabled, id, platform]);
}